import { FC, useEffect, useMemo, useState } from "react";

import { AttendanceCard } from "./AttendanceCard";
import { EngagementCard } from "./EngagementCard";
import { EngagementTable } from "./EngagementTable";
import { SessionLogTable } from "./SessionLogTable";
import { SessionsCard } from "./SessionsCard";
import { StudentTalkTimeCard } from "./StudentTalkTimeCard";

import { EngagementLevel, UserEngagementDataType } from "api/api-data-types";
import { serverGetEngagementData } from "api/api-server-data";
import { ErrorScreen, LoadingScreen } from "components/LoadingScreen";
import { ALL_TIME_DATA } from "components/constants";
import { t } from "i18n/i18n";
import { SortableTableDateRange, SortableTableDateRangeOption } from "mds/components/SortableTable";
import { useCapabilities, useUserIdsWhoCanParticipateInSection } from "store/capability-hooks";
import { storeApi, useAppSelector } from "store/index";
import { selectCurrentSectionId, selectHasVirtualSessionsForCurrentSection } from "store/selectors";
import { usePromiseEffect } from "store/store-hooks";

export const SessionLogView: FC = () => {
  const currentSectionId = useAppSelector(selectCurrentSectionId);
  const { canAuthorSection } = useCapabilities();

  useEffect(() => {
    if (currentSectionId) {
      storeApi.class_session_summaries.list({ section_id: currentSectionId });
      storeApi.class_session_user_summaries.list({ section_id: currentSectionId });
    }
  }, [currentSectionId]);
  const [selectedDateRange, setSelectedDateRange] = useState<SortableTableDateRangeOption | null>(
    null,
  );
  const effectiveDateRangeValue = useMemo(
    () => selectedDateRange?.value ?? ALL_TIME_DATA,
    [selectedDateRange],
  );

  const hasVirtualSessions = useAppSelector(selectHasVirtualSessionsForCurrentSection);

  const {
    data: engagementData,
    isLoading: isLoadingEngagementData,
    error: errorEngagementData,
  } = usePromiseEffect<Record<number, Record<string, UserEngagementDataType>> | null>(async () => {
    if (!currentSectionId || !canAuthorSection) {
      return null;
    }
    return serverGetEngagementData(currentSectionId);
  }, [currentSectionId, canAuthorSection]);

  // Filter engagement data by selected date range.
  const filteredEngagementData = useMemo(() => {
    if (!engagementData) return null;
    const selectedRange = selectedDateRange?.value ?? ALL_TIME_DATA;
    return engagementData[selectedRange] ?? null;
  }, [engagementData, selectedDateRange]);

  // Filter out non-student users from engagement data.
  const studentUserIds = useUserIdsWhoCanParticipateInSection();
  const studentEngagementData = useMemo(() => {
    if (!filteredEngagementData) return null;
    return Object.fromEntries(
      Object.entries(filteredEngagementData).filter(([userId]) => studentUserIds.includes(userId)),
    );
  }, [filteredEngagementData, studentUserIds]);

  const hasEngagementData = useMemo(() => {
    if (!engagementData) return false;
    const allTimeEngagementData = engagementData[ALL_TIME_DATA] ?? {};
    const userEntries = Object.entries(allTimeEngagementData);
    if (userEntries.length === 0) return false;
    return userEntries.some(
      ([userId, data]) =>
        studentUserIds.includes(userId) &&
        (data.effort_level !== EngagementLevel.NONE ||
          data.performance_level !== EngagementLevel.NONE),
    );
  }, [engagementData, studentUserIds]);

  if (isLoadingEngagementData) {
    return <LoadingScreen>{t("common.loading")}...</LoadingScreen>;
  }
  if (errorEngagementData) {
    return <ErrorScreen>Error loading session logs</ErrorScreen>;
  }

  return (
    <main className="mt-4">
      <div className="mb-8 flex w-full items-center justify-between rounded-lg bg-black-tint-97 px-4 py-2">
        <div className="flex items-center gap-3">
          <p className="text-sm font-medium text-black-tint-20">Showing data from:</p>
          <SortableTableDateRange value={selectedDateRange} onChange={setSelectedDateRange} />
        </div>
        <p className="text-sm font-medium text-black-tint-55">
          {t("session_log_view.applies_to_all_metrics")}
        </p>
      </div>
      <div className="flex flex-col gap-10">
        {canAuthorSection && (
          <div className="flex w-full gap-4">
            <div className="flex-1">
              <SessionsCard dateRangeValue={effectiveDateRangeValue} />
            </div>
            <div className="flex-1">
              <AttendanceCard dateRangeValue={effectiveDateRangeValue} />
            </div>
            {hasEngagementData && (
              <div className="flex-1">
                <EngagementCard engagementData={studentEngagementData} />
              </div>
            )}
            {hasVirtualSessions && (
              <div className="flex-1">
                <StudentTalkTimeCard dateRangeValue={effectiveDateRangeValue} />
              </div>
            )}
          </div>
        )}
        <SessionLogTable dateRangeValue={effectiveDateRangeValue} />
        {canAuthorSection && (
          <EngagementTable
            dateRangeValue={effectiveDateRangeValue}
            engagementData={studentEngagementData}
          />
        )}
      </div>
    </main>
  );
};
